/**
 * Validates FeatureCardsWithModalSlider component props
 * @param {object} props - The props object to validate
 * @param {boolean} [throwOnError=false] - If true, throws an error on validation failure. If false, returns validation result
 * @return {{isValid: boolean, errors: string[]}} ValidationResult object containing isValid flag and array of error messages
 * @throws {Error} if throwOnError is true and validation fails
 */
export const validateFeatureCardsWithModalSliderProps = (
  props,
  throwOnError = false
) => {
  const errors = []

  // Check if props is an object
  if (!props || typeof props !== "object") {
    errors.push("Props must be an object")
    if (throwOnError) {
      throw new Error(
        `FeatureCardsWithModalSlider validation failed:\n${errors.join("\n")}`
      )
    }
    return { isValid: false, errors }
  }

  // Validate required heading
  if (!props.heading) {
    errors.push("heading is required")
  } else {
    if (typeof props.heading !== "object") {
      errors.push("heading must be an object")
    } else {
      if (!props.heading.text) {
        errors.push("heading.text is required")
      }
      if (
        typeof props.heading.text !== "string" &&
        typeof props.heading.text !== "object"
      ) {
        errors.push("heading.text must be a string or React element")
      }
    }
  }

  // Validate required cards
  if (!props.cards) {
    errors.push("cards is required")
  } else if (!Array.isArray(props.cards)) {
    errors.push("cards must be an array")
  } else if (props.cards.length === 0) {
    errors.push("cards array must not be empty")
  } else {
    props.cards.forEach((card, index) => {
      if (typeof card !== "object" || card === null) {
        errors.push(`cards[${index}] must be an object`)
      } else {
        // Validate required image
        if (!card.image) {
          errors.push(`cards[${index}].image is required`)
        } else {
          if (typeof card.image !== "object") {
            errors.push(`cards[${index}].image must be an object`)
          } else {
            if (!card.image.src) {
              errors.push(`cards[${index}].image.src is required`)
            }
          }
        }

        // Validate required heading
        if (!card.heading) {
          errors.push(`cards[${index}].heading is required`)
        } else {
          if (typeof card.heading !== "object") {
            errors.push(`cards[${index}].heading must be an object`)
          } else {
            if (!card.heading.text) {
              errors.push(`cards[${index}].heading.text is required`)
            }
          }
        }

        // Validate optional imageActionCTA
        if (card.imageActionCTA !== undefined) {
          if (
            typeof card.imageActionCTA !== "object" ||
            card.imageActionCTA === null
          ) {
            errors.push(
              `cards[${index}].imageActionCTA must be an object if provided`
            )
          }
        }

        // Validate optional modalContent
        if (card.modalContent !== undefined) {
          if (
            typeof card.modalContent !== "object" ||
            card.modalContent === null
          ) {
            errors.push(
              `cards[${index}].modalContent must be an object if provided`
            )
          } else {
            // Validate optional heading in modalContent
            if (card.modalContent.heading !== undefined) {
              if (
                typeof card.modalContent.heading !== "object" ||
                card.modalContent.heading === null
              ) {
                errors.push(
                  `cards[${index}].modalContent.heading must be an object if provided`
                )
              } else {
                if (!card.modalContent.heading.text) {
                  errors.push(
                    `cards[${index}].modalContent.heading.text is required when heading is provided`
                  )
                }
              }
            }

            // Validate required content array
            if (!card.modalContent.content) {
              errors.push(`cards[${index}].modalContent.content is required`)
            } else if (!Array.isArray(card.modalContent.content)) {
              errors.push(
                `cards[${index}].modalContent.content must be an array`
              )
            } else {
              card.modalContent.content.forEach(
                (contentBlock, contentIndex) => {
                  if (
                    typeof contentBlock !== "object" ||
                    contentBlock === null
                  ) {
                    errors.push(
                      `cards[${index}].modalContent.content[${contentIndex}] must be an object`
                    )
                  } else {
                    // Validate optional heading in content block
                    if (contentBlock.heading !== undefined) {
                      if (
                        typeof contentBlock.heading !== "object" ||
                        contentBlock.heading === null
                      ) {
                        errors.push(
                          `cards[${index}].modalContent.content[${contentIndex}].heading must be an object if provided`
                        )
                      } else {
                        if (!contentBlock.heading.text) {
                          errors.push(
                            `cards[${index}].modalContent.content[${contentIndex}].heading.text is required when heading is provided`
                          )
                        }
                      }
                    }

                    // Validate optional copy in content block
                    if (contentBlock.copy !== undefined) {
                      if (
                        typeof contentBlock.copy !== "object" ||
                        contentBlock.copy === null
                      ) {
                        errors.push(
                          `cards[${index}].modalContent.content[${contentIndex}].copy must be an object if provided`
                        )
                      } else {
                        if (!contentBlock.copy.text) {
                          errors.push(
                            `cards[${index}].modalContent.content[${contentIndex}].copy.text is required when copy is provided`
                          )
                        }
                      }
                    }

                    // Validate optional image in content block
                    if (contentBlock.image !== undefined) {
                      if (
                        typeof contentBlock.image !== "object" ||
                        contentBlock.image === null
                      ) {
                        errors.push(
                          `cards[${index}].modalContent.content[${contentIndex}].image must be an object if provided`
                        )
                      } else {
                        if (!contentBlock.image.src) {
                          errors.push(
                            `cards[${index}].modalContent.content[${contentIndex}].image.src is required when image is provided`
                          )
                        }
                      }
                    }

                    // Validate optional isFullWidth
                    if (contentBlock.isFullWidth !== undefined) {
                      if (typeof contentBlock.isFullWidth !== "boolean") {
                        errors.push(
                          `cards[${index}].modalContent.content[${contentIndex}].isFullWidth must be a boolean if provided`
                        )
                      }
                    }
                  }
                }
              )
            }

            // Validate optional onModalClose
            if (card.modalContent.onModalClose !== undefined) {
              if (typeof card.modalContent.onModalClose !== "function") {
                errors.push(
                  `cards[${index}].modalContent.onModalClose must be a function if provided`
                )
              }
            }
          }
        }
      }
    })
  }

  // Validate optional topSpacer
  if (props.topSpacer !== undefined) {
    if (typeof props.topSpacer !== "object" || props.topSpacer === null) {
      errors.push("topSpacer must be an object if provided")
    }
  }

  // Validate optional bottomSpacer
  if (props.bottomSpacer !== undefined) {
    if (typeof props.bottomSpacer !== "object" || props.bottomSpacer === null) {
      errors.push("bottomSpacer must be an object if provided")
    }
  }

  const isValid = errors.length === 0

  if (!isValid && throwOnError) {
    throw new Error(
      `FeatureCardsWithModalSlider validation failed:\n${errors.join("\n")}`
    )
  }

  return { isValid, errors }
}
